import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Wallet, Plus, Trash2, TrendingUp, TrendingDown, DollarSign, 
  PieChart, LineChart, ArrowUpRight, ArrowDownRight, Edit2, BarChart3
} from "lucide-react";
import { toast } from "sonner";
import { LineChart as RechartsLine, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Area, AreaChart } from "recharts";
import api from "@/lib/api";

const ICONS = {
  cocoa: "🍫", coffee: "☕", sugar: "🍬", cotton: "🧵", wheat: "🌾", 
  maize: "🌽", soybean: "🫘", palm_oil: "🌴", gold: "🥇", crude_oil: "🛢️",
  copper: "🔶", natural_gas: "🔥", silver: "🥈", zinc: "⚪"
};

export default function PortfolioPage() {
  const [portfolio, setPortfolio] = useState({ positions: [], summary: {} });
  const [transactions, setTransactions] = useState([]);
  const [performance, setPerformance] = useState({ performance: [], benchmark: [], summary: {} });
  const [commodities, setCommodities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPosition, setEditingPosition] = useState(null);
  const [performancePeriod, setPerformancePeriod] = useState("30d");
  
  const [newPosition, setNewPosition] = useState({
    commodity_id: "",
    quantity: "",
    buy_price: "",
    notes: ""
  });

  const fetchData = useCallback(async () => {
    try {
      const [portfolioRes, commoditiesRes] = await Promise.all([
        api.get("/portfolio"),
        api.get("/price-alerts/commodities")
      ]);
      setPortfolio(portfolioRes.data);
      setCommodities(commoditiesRes.data.commodities || []);
    } catch (err) {
      console.error("Error fetching portfolio:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPerformance = useCallback(async () => {
    try {
      const res = await api.get(`/portfolio/performance?period=${performancePeriod}`);
      setPerformance(res.data);
    } catch (err) {
      console.error("Error fetching performance:", err);
    }
  }, [performancePeriod]);

  const fetchTransactions = useCallback(async () => {
    try {
      const res = await api.get("/portfolio/transactions?limit=20");
      setTransactions(res.data.transactions || []);
    } catch (err) {
      console.error("Error fetching transactions:", err);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchTransactions();
  }, [fetchData, fetchTransactions]);

  useEffect(() => {
    if (portfolio.positions.length > 0) {
      fetchPerformance();
    }
  }, [performancePeriod, portfolio.positions.length, fetchPerformance]);

  const handleAddPosition = async () => {
    try {
      await api.post("/portfolio/positions", {
        commodity_id: newPosition.commodity_id,
        quantity: parseFloat(newPosition.quantity),
        buy_price: parseFloat(newPosition.buy_price),
        notes: newPosition.notes || null
      });
      
      toast.success("Position added!");
      setDialogOpen(false);
      setNewPosition({ commodity_id: "", quantity: "", buy_price: "", notes: "" });
      fetchData();
      fetchTransactions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add position");
    }
  };

  const handleUpdatePosition = async () => {
    if (!editingPosition) return;
    
    try {
      await api.put(`/portfolio/positions/${editingPosition.id}`, {
        quantity: parseFloat(editingPosition.quantity),
        buy_price: parseFloat(editingPosition.buy_price),
        notes: editingPosition.notes
      });
      
      toast.success("Position updated!");
      setEditingPosition(null);
      fetchData();
      fetchTransactions();
    } catch (err) {
      toast.error("Failed to update position");
    }
  };

  const handleDeletePosition = async (positionId) => {
    try {
      await api.delete(`/portfolio/positions/${positionId}`);
      toast.success("Position closed");
      fetchData();
      fetchTransactions();
    } catch (err) {
      toast.error("Failed to close position");
    }
  };

  const selectedCommodity = commodities.find(c => c.id === newPosition.commodity_id);
  const { summary } = portfolio;

  return (
    <div data-testid="portfolio-page" className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-wider text-zinc-100">
            Portfolio
          </h1>
          <p className="text-xs text-zinc-500 font-mono mt-1">
            Track your commodity positions and performance
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-none bg-[#00ff41] text-black hover:bg-[#00ff41]/80 font-mono text-xs gap-2">
              <Plus className="w-4 h-4" /> Add Position
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 rounded-none max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-lg uppercase tracking-wider text-zinc-100">
                Add Position
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <label className="text-[10px] text-zinc-500 uppercase font-mono mb-2 block">Commodity</label>
                <Select value={newPosition.commodity_id} onValueChange={(v) => setNewPosition(p => ({ ...p, commodity_id: v }))}>
                  <SelectTrigger className="rounded-none border-zinc-700 bg-zinc-950">
                    <SelectValue placeholder="Select commodity" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700 rounded-none">
                    {commodities.map(c => (
                      <SelectItem key={c.id} value={c.id} className="font-mono">
                        {ICONS[c.id] || "📊"} {c.name} - ${c.price.toLocaleString()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase font-mono mb-2 block">Quantity</label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="e.g., 10"
                    value={newPosition.quantity}
                    onChange={(e) => setNewPosition(p => ({ ...p, quantity: e.target.value }))}
                    className="rounded-none border-zinc-700 bg-zinc-950 text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase font-mono mb-2 block">Buy Price</label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder={selectedCommodity ? `Current: $${selectedCommodity.price}` : "e.g., 2400"}
                    value={newPosition.buy_price}
                    onChange={(e) => setNewPosition(p => ({ ...p, buy_price: e.target.value }))}
                    className="rounded-none border-zinc-700 bg-zinc-950 text-sm font-mono"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-[10px] text-zinc-500 uppercase font-mono mb-2 block">Notes</label>
                <Input
                  placeholder="Optional notes..."
                  value={newPosition.notes}
                  onChange={(e) => setNewPosition(p => ({ ...p, notes: e.target.value }))}
                  className="rounded-none border-zinc-700 bg-zinc-950 text-sm font-mono"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none border-zinc-700 font-mono text-xs">
                Cancel
              </Button>
              <Button onClick={handleAddPosition} className="rounded-none bg-[#00ff41] text-black hover:bg-[#00ff41]/80 font-mono text-xs">
                Add Position
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <Wallet className="w-4 h-4 text-[#00ff41]" />
              <p className="text-[10px] text-zinc-500 uppercase font-mono">Total Value</p>
            </div>
            <p className="text-2xl font-mono font-bold text-zinc-100">
              ${summary.total_value?.toLocaleString() || "0"}
            </p>
          </CardContent>
        </Card>
        
        <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="w-4 h-4 text-zinc-400" />
              <p className="text-[10px] text-zinc-500 uppercase font-mono">Cost Basis</p>
            </div>
            <p className="text-2xl font-mono font-bold text-zinc-400">
              ${summary.total_cost?.toLocaleString() || "0"}
            </p>
          </CardContent>
        </Card>
        
        <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              {(summary.total_pnl || 0) >= 0 ? (
                <TrendingUp className="w-4 h-4 text-[#00ff41]" />
              ) : (
                <TrendingDown className="w-4 h-4 text-[#ff3b30]" />
              )}
              <p className="text-[10px] text-zinc-500 uppercase font-mono">Total P&L</p>
            </div>
            <p className={`text-2xl font-mono font-bold ${(summary.total_pnl || 0) >= 0 ? 'text-[#00ff41]' : 'text-[#ff3b30]'}`}>
              {(summary.total_pnl || 0) >= 0 ? '+' : ''}${summary.total_pnl?.toLocaleString() || "0"}
            </p>
          </CardContent>
        </Card>
        
        <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <PieChart className="w-4 h-4 text-blue-400" />
              <p className="text-[10px] text-zinc-500 uppercase font-mono">Return %</p>
            </div>
            <p className={`text-2xl font-mono font-bold ${(summary.total_pnl_pct || 0) >= 0 ? 'text-[#00ff41]' : 'text-[#ff3b30]'}`}>
              {(summary.total_pnl_pct || 0) >= 0 ? '+' : ''}{summary.total_pnl_pct?.toFixed(2) || "0"}%
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="positions" className="space-y-4">
        <TabsList className="bg-zinc-900 border border-zinc-800 rounded-none p-1">
          <TabsTrigger value="positions" className="rounded-none data-[state=active]:bg-zinc-800 data-[state=active]:text-[#00ff41] font-mono text-xs">
            <Wallet className="w-3.5 h-3.5 mr-2" /> Positions
          </TabsTrigger>
          <TabsTrigger value="performance" className="rounded-none data-[state=active]:bg-zinc-800 data-[state=active]:text-[#00ff41] font-mono text-xs">
            <LineChart className="w-3.5 h-3.5 mr-2" /> Performance
          </TabsTrigger>
          <TabsTrigger value="transactions" className="rounded-none data-[state=active]:bg-zinc-800 data-[state=active]:text-[#00ff41] font-mono text-xs">
            <BarChart3 className="w-3.5 h-3.5 mr-2" /> Transactions
          </TabsTrigger>
        </TabsList>

        <TabsContent value="positions">
          <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
            <CardHeader className="pb-2 px-4 pt-4">
              <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400">
                Your Positions ({portfolio.positions.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin w-6 h-6 border-2 border-[#00ff41] border-t-transparent rounded-full mx-auto" />
                </div>
              ) : portfolio.positions.length === 0 ? (
                <div className="p-8 text-center">
                  <Wallet className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-500 font-mono text-sm">No positions yet</p>
                  <p className="text-zinc-600 text-xs mt-1">Add your first position to start tracking</p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="divide-y divide-zinc-800">
                    {portfolio.positions.map(pos => (
                      <div key={pos.id} className="p-4 hover:bg-zinc-900/50 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{ICONS[pos.commodity_id] || "📊"}</span>
                            <div>
                              <p className="font-mono font-medium text-zinc-100">{pos.commodity_name}</p>
                              <p className="text-xs text-zinc-500 font-mono">
                                {pos.quantity} units @ ${pos.buy_price.toLocaleString()}
                              </p>
                            </div>
                          </div>
                          
                          <div className="text-right">
                            <p className="font-mono font-bold text-zinc-100">${pos.current_value.toLocaleString()}</p>
                            <div className={`flex items-center justify-end gap-1 text-xs font-mono ${pos.pnl >= 0 ? 'text-[#00ff41]' : 'text-[#ff3b30]'}`}>
                              {pos.pnl >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                              {pos.pnl >= 0 ? '+' : ''}${pos.pnl.toLocaleString()} ({pos.pnl_pct.toFixed(2)}%)
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2 ml-4">
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-none border-zinc-700 h-8 w-8 p-0"
                              onClick={() => setEditingPosition(pos)}
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-none border-zinc-700 h-8 w-8 p-0 text-[#ff3b30] hover:bg-[#ff3b30]/10"
                              onClick={() => handleDeletePosition(pos.id)}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performance">
          <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
            <CardHeader className="pb-2 px-4 pt-4">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400">
                  Portfolio Performance
                </CardTitle>
                <Select value={performancePeriod} onValueChange={setPerformancePeriod}>
                  <SelectTrigger className="w-24 rounded-none border-zinc-700 bg-zinc-950 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700 rounded-none">
                    <SelectItem value="7d">7 Days</SelectItem>
                    <SelectItem value="30d">30 Days</SelectItem>
                    <SelectItem value="90d">90 Days</SelectItem>
                    <SelectItem value="1y">1 Year</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              {portfolio.positions.length === 0 ? (
                <div className="h-[300px] flex items-center justify-center">
                  <p className="text-zinc-500 font-mono text-sm">Add positions to see performance</p>
                </div>
              ) : (
                <>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={performance.performance}>
                        <defs>
                          <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#00ff41" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#00ff41" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                        <XAxis dataKey="date" stroke="#666" fontSize={10} tickFormatter={(v) => v.slice(5)} />
                        <YAxis stroke="#666" fontSize={10} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: 0 }}
                          labelStyle={{ color: '#888' }}
                          formatter={(value) => [`$${value.toLocaleString()}`, 'Value']}
                        />
                        <Legend />
                        <Area type="monotone" dataKey="value" name="Portfolio" stroke="#00ff41" fill="url(#colorValue)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                    <div className="bg-zinc-900 p-3 rounded-none border border-zinc-800">
                      <p className="text-[10px] text-zinc-500 uppercase font-mono">Start Value</p>
                      <p className="font-mono font-bold text-zinc-100">${performance.summary?.start_value?.toLocaleString() || 0}</p>
                    </div>
                    <div className="bg-zinc-900 p-3 rounded-none border border-zinc-800">
                      <p className="text-[10px] text-zinc-500 uppercase font-mono">Current Value</p>
                      <p className="font-mono font-bold text-zinc-100">${performance.summary?.current_value?.toLocaleString() || 0}</p>
                    </div>
                    <div className="bg-zinc-900 p-3 rounded-none border border-zinc-800">
                      <p className="text-[10px] text-zinc-500 uppercase font-mono">Period Change</p>
                      <p className={`font-mono font-bold ${(performance.summary?.change || 0) >= 0 ? 'text-[#00ff41]' : 'text-[#ff3b30]'}`}>
                        {(performance.summary?.change || 0) >= 0 ? '+' : ''}${performance.summary?.change?.toLocaleString() || 0}
                      </p>
                    </div>
                    <div className="bg-zinc-900 p-3 rounded-none border border-zinc-800">
                      <p className="text-[10px] text-zinc-500 uppercase font-mono">Period Return</p>
                      <p className={`font-mono font-bold ${(performance.summary?.change_pct || 0) >= 0 ? 'text-[#00ff41]' : 'text-[#ff3b30]'}`}>
                        {(performance.summary?.change_pct || 0) >= 0 ? '+' : ''}{performance.summary?.change_pct?.toFixed(2) || 0}%
                      </p>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="transactions">
          <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
            <CardHeader className="pb-2 px-4 pt-4">
              <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400">
                Transaction History
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {transactions.length === 0 ? (
                <div className="p-8 text-center">
                  <BarChart3 className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-500 font-mono text-sm">No transactions yet</p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="divide-y divide-zinc-800">
                    {transactions.map(tx => (
                      <div key={tx.id} className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${tx.type === 'BUY' ? 'bg-[#00ff41]/20' : 'bg-[#ff3b30]/20'}`}>
                            {tx.type === 'BUY' ? (
                              <ArrowUpRight className={`w-4 h-4 text-[#00ff41]`} />
                            ) : (
                              <ArrowDownRight className={`w-4 h-4 text-[#ff3b30]`} />
                            )}
                          </div>
                          <div>
                            <p className="font-mono font-medium text-zinc-100">
                              {tx.type} {tx.commodity_id?.replace("_", " ").toUpperCase()}
                            </p>
                            <p className="text-xs text-zinc-500 font-mono">
                              {tx.quantity} units @ ${tx.price?.toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-mono font-bold ${tx.type === 'BUY' ? 'text-[#00ff41]' : 'text-[#ff3b30]'}`}>
                            ${tx.total?.toLocaleString()}
                          </p>
                          <p className="text-xs text-zinc-500 font-mono">
                            {new Date(tx.timestamp).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Position Dialog */}
      <Dialog open={!!editingPosition} onOpenChange={() => setEditingPosition(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 rounded-none max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg uppercase tracking-wider text-zinc-100">
              Edit Position
            </DialogTitle>
          </DialogHeader>
          
          {editingPosition && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-3 p-3 bg-zinc-950 rounded-none border border-zinc-800">
                <span className="text-2xl">{ICONS[editingPosition.commodity_id] || "📊"}</span>
                <span className="font-mono font-medium text-zinc-100">{editingPosition.commodity_name}</span>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase font-mono mb-2 block">Quantity</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingPosition.quantity}
                    onChange={(e) => setEditingPosition(p => ({ ...p, quantity: e.target.value }))}
                    className="rounded-none border-zinc-700 bg-zinc-950 text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase font-mono mb-2 block">Buy Price</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingPosition.buy_price}
                    onChange={(e) => setEditingPosition(p => ({ ...p, buy_price: e.target.value }))}
                    className="rounded-none border-zinc-700 bg-zinc-950 text-sm font-mono"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-[10px] text-zinc-500 uppercase font-mono mb-2 block">Notes</label>
                <Input
                  value={editingPosition.notes || ""}
                  onChange={(e) => setEditingPosition(p => ({ ...p, notes: e.target.value }))}
                  className="rounded-none border-zinc-700 bg-zinc-950 text-sm font-mono"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingPosition(null)} className="rounded-none border-zinc-700 font-mono text-xs">
              Cancel
            </Button>
            <Button onClick={handleUpdatePosition} className="rounded-none bg-[#00ff41] text-black hover:bg-[#00ff41]/80 font-mono text-xs">
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
